import { requestApi } from '~/services/api-new';
import { freqToTimeConst } from './utils';
import { LIMIT_TIME_CONST_MAP } from '../constants';
import { API_PAYMENT_BOOT_SERVICE_REQUESTED_LIMITS, API_PFDS } from '~/components/account/limits/deposit/constants';
import { insertTrackEvent } from '@comeon/analytics';

export interface CXInterviewStatusResponse {
	status: string;
	result: boolean;
}

interface DepositLimit {
	freq: string;
	amount: string;
}

export type RequestedDepositLimitPayload = {
	limit: DepositLimit;
	cxChatButtonPushed?: boolean;
};

export interface DepositLimitUsageData {
	timeConst: number;
	limit: number;
	isHalfExceeded: boolean;
}

export interface DepositLimitUsageResult {
	rgDepositLimitPercentReachedData: DepositLimitUsageData[];
}

export interface DepositLimitUsageResponse {
	status: string;
	result: DepositLimitUsageResult;
}

export interface LimitInfo {
	type: string;
	limit: number;
}

const getCXInterviewStatus = async () => {
	try {
		const url = `${API_PAYMENT_BOOT_SERVICE_REQUESTED_LIMITS}/playerRequestedDepositLimit/getPlayerShouldBeRemindedToFinalizeInterview`;
		const response = await requestApi<CXInterviewStatusResponse>({ endpoint: url });
		return response;
	} catch {
		// handle error
		return false;
	}
};

const updateRequestedDepositLimits = async ({ limit, cxChatButtonPushed = false }: RequestedDepositLimitPayload) => {
	try {
		const url = `${API_PAYMENT_BOOT_SERVICE_REQUESTED_LIMITS}/playerRequestedDepositLimit/savePlayerRequestedDepositLimitsFromProfilePage`;
		const payload = {
			cxChatButtonPushed,
			timeConst: freqToTimeConst(limit.freq),
			limitValue: limit.amount
		};
		const response: { status: string } = await requestApi({
			endpoint: url,
			method: 'POST',
			payload
		});

		if (response.status === 'SUCCESS') {
			const freqText = {
				day: 'Daily',
				week: 'Weekly',
				month: 'Monthly'
			};

			const freqLabel = Object.keys(freqText).includes(limit.freq)
				? `${freqText[limit.freq as keyof typeof freqText]}`
				: '';

			insertTrackEvent({
				category: 'Sidebar',
				action: 'Limit setup',
				label: `${freqLabel} - deposit - account - manual`,
				value: limit.amount
			});
		}

		return response;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('savePlayerRequestedDepositLimitsFromProfilePage => error:', error);
		return undefined;
	}
};

const getDepositLimitUsage = async () => {
	try {
		const response = await requestApi<DepositLimitUsageResponse>({
			endpoint: `${API_PFDS}/depositlimitsusage/getDepositLimitUsage`
		});
		return response;
	} catch {
		return {
			status: 'FAULURE',
			result: {
				rgDepositLimitPercentReachedData: []
			}
		};
	}
};

const checkDepositLimitsHalfExceeded = async (): Promise<LimitInfo[]> => {
	try {
		const response: DepositLimitUsageResponse = await getDepositLimitUsage();

		if (response.status === 'SUCCESS') {
			const { rgDepositLimitPercentReachedData } = response.result ?? {};

			return (
				rgDepositLimitPercentReachedData
					?.filter((value: DepositLimitUsageData) => value.isHalfExceeded)
					.map((value: DepositLimitUsageData) => ({
						type: LIMIT_TIME_CONST_MAP[value.timeConst] || 'UNKNOWN',
						limit: value.limit
					})) ?? []
			);
		}

		// handle other failure cases explicitly
		return [];
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Error checking deposit limits usage:', error);

		// Ensure we always return a value
		return [];
	}
};

export { getCXInterviewStatus, updateRequestedDepositLimits, getDepositLimitUsage, checkDepositLimitsHalfExceeded };

export default {};
